import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import type { SocialTermsInputProps } from '@/types/components/social-tracker-dialog'
import { addSocialTerms } from './socialTrackerForm'

/** Draft text belongs to the wizard, so disclosure and step navigation cannot lose it. */
export function SocialTermsInput({
  id,
  values,
  draft,
  onDraftChange,
  onChange,
  placeholder,
  testId,
  isInvalid,
  describedBy,
}: SocialTermsInputProps) {
  const { t } = useTranslation('app')
  function commit() {
    if (!draft.trim()) return
    onChange(addSocialTerms(values, draft))
    onDraftChange('')
  }
  return (
    <div className="flex min-h-11 flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-input bg-background px-3 py-1 focus-within:border-primary/45">
      <Input
        id={id}
        value={draft}
        className="h-9 min-w-[100px] flex-1 basis-24 border-0 px-0 shadow-none focus-visible:ring-0"
        placeholder={values.length ? undefined : placeholder}
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedBy}
        data-testid={testId}
        onChange={(event) => onDraftChange(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.nativeEvent.isComposing) return
          if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault()
            commit()
          }
        }}
      />
      {values.length > 0 ? (
        <ul
          className="ml-auto flex max-h-28 max-w-full flex-wrap justify-end gap-1.5 overflow-y-auto"
          data-testid={`${testId}-values`}
        >
          {values.map((value) => (
            <li
              key={value}
              className="inline-flex max-w-full items-center gap-1 rounded-md bg-muted px-2.5 py-1 text-sm text-foreground"
            >
              <span className="truncate">{value}</span>
              <button
                type="button"
                className="shrink-0 rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={t('socialListening.form.removeTermAria', {
                  term: value,
                })}
                data-testid={`${testId}-remove-${value}`}
                onClick={() =>
                  onChange(values.filter((item) => item !== value))
                }
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
